import React, { useEffect, useState } from 'react';
import PT from 'prop-types';
import {
  Box,
  useI18nContext,
  Link,
  Pill,
  colors,
  spacing,
  Flex,
  Typography,
} from '@procore/core-react';
import { ExternalLink } from '@procore/core-icons';
import styled from 'styled-components';
import { useAppContext } from '@/react/context/AppContext';
import { RegionModal } from '../utils/RegionModal';
import { VersionHistoryModal } from '../utils/VersionHistoryModal';
import { AppType, EmbeddedType, hasEmbedded } from '../utils/AppType';
import { UnderlineLink } from '@/react/styles/v2';

const CategoryPill = styled(Pill)`
  color: white;
  background-color: ${colors.blue50};
  font-weight: 400;
  font-size: 18px;
  line-height: 24px;
  letter-spacing: 0.48px;
  border-radius: 16px;
  padding: ${spacing.xxs}px ${spacing.md}px;
`;

const SectionLabel = styled(Typography)`
  color: ${colors.black};
  font-feature-settings:
    'liga' off,
    'clig' off;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: 0.64px;
  text-transform: uppercase;
`;

const RowGapFlex = styled(Flex)`
  row-gap: ${spacing.sm}px;
`;

const SideBarSection = ({ title, children, testId }) => (
  <Flex paddingBottom="xl" gap="6px" direction="column" data-testid={testId}>
    <SectionLabel intent="label" className="font-pitch-sans">
      {title}
    </SectionLabel>
    {children}
  </Flex>
);

SideBarSection.propTypes = {
  title: PT.string.isRequired,
  children: PT.node.isRequired,
  testId: PT.string.isRequired,
};

const SideBar = () => {
  const i18n = useI18nContext();

  const {
    appDetails: { app, version },
  } = useAppContext();

  const {
    built_by,
    name,
    website,
    category_names,
    regions,
    countries,
    versioning_history,
    components,
  } = app;

  // last version in the versioning history array
  const latestVersion =
    versioning_history?.[versioning_history.length - 1]?.semantic_version;

  const [hasEmbeddedComponents, setHasEmbeddedComponents] = useState(
    hasEmbedded(components)
  );
  useEffect(() => {
    setHasEmbeddedComponents(hasEmbedded(components));
  }, [components]);

  const displayVersion = version || latestVersion;

  return (
    <div data-testid="app-sidebar">
      <Box marginTop="md">
        <SideBarSection
          title={i18n.t('appDetails.titles.appType')}
          testId="app-type"
        >
          <AppType components={components} />
        </SideBarSection>
        {hasEmbeddedComponents && (
          <SideBarSection
            title={i18n.t('appDetails.titles.embeddedType')}
            testId="app-embedded-type"
          >
            <EmbeddedType components={components} />
          </SideBarSection>
        )}
        <SideBarSection
          title={i18n.t('appDetails.titles.category')}
          testId="app-categories"
        >
          <RowGapFlex wrap="wrap" rowGap="sm">
            {category_names?.map((category) => (
              <Box key={category} marginRight="sm">
                <CategoryPill>{category}</CategoryPill>
              </Box>
            ))}
          </RowGapFlex>
        </SideBarSection>
        <SideBarSection
          title={i18n.t('appDetails.titles.availableIn')}
          testId="app-regions"
        >
          {regions?.length === 0 ? (
            <Typography intent="body" className="font-size-body color-concrete">
              {i18n.t('appDetails.noRegions')}
            </Typography>
          ) : (
            <>
              <Box gap="sm">
                {regions?.map((region) => (
                  <Typography
                    key={region}
                    intent="body"
                    className="font-size-body color-concrete"
                  >{`${region}${
                    countries[region] ? ` (${countries[region].length})` : ''
                  }; `}</Typography>
                ))}
              </Box>
              <RegionModal countries={countries} regions={regions} />
            </>
          )}
        </SideBarSection>
        {displayVersion && (
          <SideBarSection title={i18n.t('appDetails.titles.version')}>
            <Typography intent="body" className="font-size-body color-concrete">
              {displayVersion}
            </Typography>
            <VersionHistoryModal versionHistory={versioning_history} />
          </SideBarSection>
        )}
        <SideBarSection
          title={i18n.t('appDetails.titles.website')}
          testId="app-website"
        >
          {/* styling bug here around no anchor underline */}
          <UnderlineLink>
            <Link href={website} target="_blank" className="font-size-body">
              {built_by || name} <ExternalLink size="sm" />{' '}
            </Link>
          </UnderlineLink>
        </SideBarSection>
      </Box>
    </div>
  );
};

export default SideBar;
